import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "@/lib/utils";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={className}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        // Base styles - 20x20 size (inner 12px + ring 4px each side)
        "group relative size-5 shrink-0 rounded-full overflow-hidden",
        // Background and border - unchecked state
        "bg-static-white",
        "shadow-[inset_0_0_0_1px_var(--lines-normal),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
        // Checked state - green background with inset border
        "data-[state=checked]:bg-primary-normal",
        "data-[state=checked]:shadow-[inset_0_0_0_1px_rgba(43,45,47,0.1),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
        // Hover state (only when not disabled) - unchecked
        "not-disabled:hover:shadow-[inset_0_0_0_1px_var(--lines-strong),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
        // Focus state
        "focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--primary-normal)]",
        "data-[state=checked]:focus-visible:shadow-[0_0_0_2px_var(--static-white),0_0_0_4px_var(--primary-normal)]",
        // Disabled state - unchecked: lighter border
        "disabled:cursor-not-allowed",
        "disabled:shadow-[inset_0_0_0_1px_var(--labels-disable)]",
        // Disabled state - checked: gray background instead of green
        "disabled:data-[state=checked]:bg-labels-alternative disabled:data-[state=checked]:shadow-none",
        // Transition
        "transition-all duration-150",
        className,
      )}
      {...props}
    >
      {/* Dim overlay for checked state interactions */}
      <span
        className={cn(
          "pointer-events-none absolute inset-0 z-20 rounded-full transition-colors duration-150",
          // Hover: 6% dim
          "group-not-disabled:group-hover:bg-static-black/6",
          // Focus: 10% dim
          "group-not-disabled:group-focus-visible:bg-static-black/10",
          // Pressed: 14% dim
          "group-not-disabled:group-active:bg-static-black/[0.14]",
          // Disabled: no dim
          "group-disabled:bg-transparent!",
        )}
      />
      {/* Inner circle - always visible, color changes by state */}
      <span
        className={cn(
          "absolute inset-0 z-10 flex items-center justify-center pointer-events-none",
        )}
      >
        <span
          className={cn(
            "size-3 rounded-full transition-all duration-150",
            // Unchecked default: border ring matching outer border
            "bg-static-white shadow-[inset_0_0_0_1px_var(--lines-normal),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
            // Unchecked hover: stronger border
            "group-not-disabled:group-hover:shadow-[inset_0_0_0_1px_var(--lines-strong),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
            // Unchecked focus: keep same as default (outer handles focus ring)
            "group-not-disabled:group-focus-visible:shadow-[inset_0_0_0_1px_var(--lines-normal),inset_0_-1px_0_0_rgba(43,45,47,0.1)]",
            // Checked: white circle with subtle inset border
            "group-data-[state=checked]:shadow-[inset_0_0_0_1px_rgba(39,44,48,0.1)]",
            // Disabled unchecked: lighter border
            "group-disabled:shadow-[inset_0_0_0_1px_var(--labels-disable)]",
          )}
        />
      </span>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
